/**
 * field-autocomplete.ts - Inline Field Autocomplete
 *
 * Provides search-as-you-type functionality for firstName and lastName fields,
 * with FIDE/SwissChess tabs and optional club filtering.
 */

import { API_BASE_URL, PLAYER_DATABASES } from '../../utils/constants';
import { parseFideResults, parseSwissChessResults, type PlayerSearchResult } from './player-search-parsers';
import { escapeHtml } from '../../utils/security';

// Field types
type FieldType = 'firstname' | 'lastname';

// Debounce timeouts per field
const fieldTimeouts = new Map<string, number>();

// Cache for search results
const fieldSearchCache = new Map<string, PlayerSearchResult[]>();

// Track active tab and club filter per tournament+field
const activeTabState = new Map<string, string>();
const clubFilterState = new Map<string, string>();

/**
 * Get translation helper
 */
function t(key: string, fallback: string): string {
  return window.i18n?.t(key) ?? fallback;
}

/**
 * Get the tournament's time control setting
 */
function getTournamentTimeControl(tournamentId: string): string {
  const activeButton = document.querySelector(`#time-control-${tournamentId} .segment-button.active`) as HTMLElement | null;
  return activeButton?.dataset['value'] || 'classical';
}

/**
 * Get the appropriate rating based on tournament time control
 */
function getRatingForTimeControl(player: PlayerSearchResult, timeControl: string): number {
  switch (timeControl) {
    case 'blitz':
      return player.ratingBlitz || player.ratingRapid || player.ratingStd || player.rating || 0;
    case 'rapid':
      return player.ratingRapid || player.ratingStd || player.ratingBlitz || player.rating || 0;
    case 'classical':
    default:
      return player.ratingStd || player.ratingRapid || player.ratingBlitz || player.rating || 0;
  }
}

/**
 * Filter players by first name match
 */
function filterByFirstName(players: PlayerSearchResult[], searchTerm: string): PlayerSearchResult[] {
  const termLower = searchTerm.toLowerCase();
  return players.filter(p => {
    const firstNameLower = (p.firstName || '').toLowerCase();
    // Match if firstName starts with or contains the search term
    return firstNameLower.startsWith(termLower) || firstNameLower.includes(termLower);
  });
}

/**
 * Get state key for tournament+field combination
 */
function getStateKey(tournamentId: string, fieldType: FieldType): string {
  return `${tournamentId}:${fieldType}`;
}

/**
 * Setup autocomplete for a name field (firstName or lastName)
 */
function setupNameFieldAutocomplete(tournamentId: string, fieldType: FieldType): void {
  const inputId = fieldType === 'firstname' ? `player-first-name-${tournamentId}` : `player-last-name-${tournamentId}`;
  const suggestionsId = `${fieldType}-suggestions-${tournamentId}`;

  const input = document.getElementById(inputId) as HTMLInputElement | null;
  const suggestionsDiv = document.getElementById(suggestionsId);

  if (!input || !suggestionsDiv) return;

  const stateKey = getStateKey(tournamentId, fieldType);

  // Initialize state only if not already set (preserve club filter between player additions)
  if (!activeTabState.has(stateKey)) {
    activeTabState.set(stateKey, fieldType === 'firstname' ? PLAYER_DATABASES.SWISS_CHESS : PLAYER_DATABASES.FIDE);
  }
  if (!clubFilterState.has(stateKey)) {
    clubFilterState.set(stateKey, '');
  }

  input.addEventListener('input', (e) => {
    const target = e.target as HTMLInputElement;
    const searchTerm = target.value.trim();

    // Clear previous timeout for this field
    const timeoutKey = `${tournamentId}:${fieldType}`;
    const existingTimeout = fieldTimeouts.get(timeoutKey);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    if (searchTerm.length < 3) {
      suggestionsDiv.style.display = 'none';
      return;
    }

    const timeout = window.setTimeout(async () => {
      await searchPlayers(searchTerm, tournamentId, fieldType);
    }, 400);
    fieldTimeouts.set(timeoutKey, timeout);
  });

  // Close suggestions when clicking outside
  document.addEventListener('click', (e) => {
    const target = e.target as Node;
    if (!input.contains(target) && !suggestionsDiv.contains(target)) {
      suggestionsDiv.style.display = 'none';
    }
  });

  // Handle keyboard navigation
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      suggestionsDiv.style.display = 'none';
    }
  });
}

/**
 * Search for players
 */
async function searchPlayers(searchTerm: string, tournamentId: string, fieldType: FieldType): Promise<void> {
  const suggestionsId = `${fieldType}-suggestions-${tournamentId}`;
  const inputId = fieldType === 'firstname' ? `player-first-name-${tournamentId}` : `player-last-name-${tournamentId}`;

  const suggestionsDiv = document.getElementById(suggestionsId);
  const input = document.getElementById(inputId);

  if (!suggestionsDiv || !input) return;

  // Position the dropdown
  positionDropdown(input, suggestionsDiv);

  const stateKey = getStateKey(tournamentId, fieldType);
  const activeTab = activeTabState.get(stateKey) || PLAYER_DATABASES.FIDE;
  const clubFilter = clubFilterState.get(stateKey) || '';

  // Check if dropdown is already visible - if so, only update content
  const isAlreadyVisible = suggestionsDiv.style.display === 'block';
  let contentDiv = suggestionsDiv.querySelector('.field-autocomplete-content');

  if (!isAlreadyVisible || !contentDiv) {
    // Build full dropdown HTML with header
    const headerHtml = buildDropdownHeader(tournamentId, fieldType, activeTab, clubFilter);
    suggestionsDiv.innerHTML = headerHtml + '<div class="field-autocomplete-content"><div class="field-autocomplete-loading">Searching...</div></div>';
    suggestionsDiv.style.display = 'block';
    // Setup header event handlers
    setupHeaderHandlers(tournamentId, fieldType, searchTerm);
    contentDiv = suggestionsDiv.querySelector('.field-autocomplete-content');
  } else {
    // Just update the content area
    contentDiv.innerHTML = '<div class="field-autocomplete-loading">Searching...</div>';
  }

  try {
    let fidePlayers: PlayerSearchResult[];
    let swissPlayers: PlayerSearchResult[];

    if (fieldType === 'firstname') {
      // For firstName search: search by club only (if set), then filter by firstName
      // FIDE doesn't support club search, so just return empty for firstName searches
      fidePlayers = [];

      if (clubFilter) {
        // Search SwissChess by club only, then filter by firstName
        swissPlayers = await searchSwissChessByClubOnly(clubFilter);
        swissPlayers = filterByFirstName(swissPlayers, searchTerm);
      } else {
        // No club filter - show message that club is required for firstName search
        swissPlayers = [];
      }
    } else {
      // For lastName search: search normally
      [fidePlayers, swissPlayers] = await Promise.all([
        searchDatabase(searchTerm, PLAYER_DATABASES.FIDE),
        searchDatabaseWithClub(searchTerm, PLAYER_DATABASES.SWISS_CHESS, clubFilter)
      ]);
    }

    displaySuggestions(fidePlayers, swissPlayers, tournamentId, fieldType, activeTab, clubFilter);
  } catch (error) {
    console.error('Error searching for players:', error);
    const contentDiv = suggestionsDiv.querySelector('.field-autocomplete-content');
    if (contentDiv) {
      contentDiv.innerHTML = '<div class="field-autocomplete-error">Search error</div>';
    }
  }
}

/**
 * Build the dropdown header with tabs and club filter
 */
function buildDropdownHeader(tournamentId: string, fieldType: FieldType, activeTab: string, clubFilter: string): string {
  const filterId = `${fieldType}-club-filter-${tournamentId}`;
  return `
    <div class="field-autocomplete-header">
      <div class="field-autocomplete-tabs">
        <button class="field-autocomplete-tab ${activeTab === PLAYER_DATABASES.FIDE ? 'active' : ''}" data-tab="${PLAYER_DATABASES.FIDE}">${t('player.fideTab', 'FIDE')}</button>
        <button class="field-autocomplete-tab ${activeTab === PLAYER_DATABASES.SWISS_CHESS ? 'active' : ''}" data-tab="${PLAYER_DATABASES.SWISS_CHESS}">${t('player.swissChessTab', 'Swiss Chess')}</button>
      </div>
      <div class="field-autocomplete-club-filter ${activeTab === PLAYER_DATABASES.SWISS_CHESS ? '' : 'hidden'}">
        <input type="text"
          id="${filterId}"
          placeholder="${t('player.filterByClub', 'Filter by club...')}"
          value="${escapeHtml(clubFilter)}"
          autocomplete="off">
      </div>
    </div>
  `;
}

/**
 * Setup event handlers for the dropdown header
 */
function setupHeaderHandlers(tournamentId: string, fieldType: FieldType, currentSearchTerm: string): void {
  const suggestionsId = `${fieldType}-suggestions-${tournamentId}`;
  const filterId = `${fieldType}-club-filter-${tournamentId}`;
  const stateKey = getStateKey(tournamentId, fieldType);

  const suggestionsDiv = document.getElementById(suggestionsId);
  if (!suggestionsDiv) return;

  // Tab switching
  suggestionsDiv.querySelectorAll('.field-autocomplete-tab').forEach(tab => {
    tab.addEventListener('click', (e) => {
      e.stopPropagation();
      const newTab = (tab as HTMLElement).dataset['tab'];
      if (newTab) {
        activeTabState.set(stateKey, newTab);

        // Update tab active states visually
        suggestionsDiv.querySelectorAll('.field-autocomplete-tab').forEach(t => {
          if ((t as HTMLElement).dataset['tab'] === newTab) {
            t.classList.add('active');
          } else {
            t.classList.remove('active');
          }
        });

        // Show/hide club filter based on tab
        const clubFilterDiv = suggestionsDiv.querySelector('.field-autocomplete-club-filter');
        if (clubFilterDiv) {
          if (newTab === PLAYER_DATABASES.SWISS_CHESS) {
            clubFilterDiv.classList.remove('hidden');
          } else {
            clubFilterDiv.classList.add('hidden');
          }
        }

        // Re-search with new tab
        searchPlayers(currentSearchTerm, tournamentId, fieldType);
      }
    });
  });

  // Club filter input
  const clubFilterInput = document.getElementById(filterId) as HTMLInputElement | null;
  if (clubFilterInput) {
    let clubFilterTimeout: number | null = null;

    clubFilterInput.addEventListener('input', (e) => {
      e.stopPropagation();
      const target = e.target as HTMLInputElement;
      clubFilterState.set(stateKey, target.value.trim());

      if (clubFilterTimeout) {
        clearTimeout(clubFilterTimeout);
      }

      clubFilterTimeout = window.setTimeout(() => {
        searchPlayers(currentSearchTerm, tournamentId, fieldType);
      }, 400);
    });

    // Prevent closing dropdown when typing in club filter
    clubFilterInput.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    clubFilterInput.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Escape') {
        suggestionsDiv.style.display = 'none';
      }
    });
  }
}

/**
 * Search a player database
 */
async function searchDatabase(searchTerm: string, database: string): Promise<PlayerSearchResult[]> {
  const cacheKey = `${database}:${searchTerm}`;

  if (fieldSearchCache.has(cacheKey)) {
    return fieldSearchCache.get(cacheKey)!;
  }

  try {
    const endpoint = database === PLAYER_DATABASES.FIDE ? 'fide-search' : 'swisschess-search';
    const response = await fetch(`${API_BASE_URL}/${endpoint}?query=${encodeURIComponent(searchTerm)}`, {
      credentials: 'include',
    });
    const html = await response.text();
    const players = database === PLAYER_DATABASES.FIDE ? parseFideResults(html) : parseSwissChessResults(html);

    fieldSearchCache.set(cacheKey, players);
    return players;
  } catch (error) {
    console.error(`Error fetching ${database} player data:`, error);
    return [];
  }
}

/**
 * Search SwissChess database with optional club filter
 */
async function searchDatabaseWithClub(searchTerm: string, database: string, clubFilter: string): Promise<PlayerSearchResult[]> {
  if (database !== PLAYER_DATABASES.SWISS_CHESS) {
    return searchDatabase(searchTerm, database);
  }

  // If no club filter, use regular search
  if (!clubFilter) {
    return searchDatabase(searchTerm, database);
  }

  const cacheKey = `${database}:${searchTerm}:club:${clubFilter}`;

  if (fieldSearchCache.has(cacheKey)) {
    return fieldSearchCache.get(cacheKey)!;
  }

  try {
    // Search SwissChess with club parameter
    const response = await fetch(
      `${API_BASE_URL}/swisschess-search?query=${encodeURIComponent(searchTerm)}&club=${encodeURIComponent(clubFilter)}`,
      { credentials: 'include' }
    );
    const html = await response.text();
    const players = parseSwissChessResults(html);

    fieldSearchCache.set(cacheKey, players);
    return players;
  } catch (error) {
    console.error(`Error fetching ${database} player data with club filter:`, error);
    return [];
  }
}

/**
 * Search SwissChess database by club only (for firstName searches)
 */
async function searchSwissChessByClubOnly(clubName: string): Promise<PlayerSearchResult[]> {
  const cacheKey = `swisschess:clubonly:${clubName}`;

  if (fieldSearchCache.has(cacheKey)) {
    return fieldSearchCache.get(cacheKey)!;
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/swisschess-search?club=${encodeURIComponent(clubName)}`,
      { credentials: 'include' }
    );
    const html = await response.text();
    const players = parseSwissChessResults(html);

    fieldSearchCache.set(cacheKey, players);
    return players;
  } catch (error) {
    console.error('Error fetching SwissChess players by club:', error);
    return [];
  }
}

/**
 * Position dropdown below the input field
 */
function positionDropdown(input: HTMLElement, dropdown: HTMLElement): void {
  const rect = input.getBoundingClientRect();
  dropdown.style.top = `${rect.bottom + 2}px`;
  dropdown.style.left = `${rect.left}px`;
}

/**
 * Display suggestions with tabs
 */
function displaySuggestions(
  fidePlayers: PlayerSearchResult[],
  swissPlayers: PlayerSearchResult[],
  tournamentId: string,
  fieldType: FieldType,
  activeTab: string,
  clubFilter: string
): void {
  const suggestionsId = `${fieldType}-suggestions-${tournamentId}`;
  const inputId = fieldType === 'firstname' ? `player-first-name-${tournamentId}` : `player-last-name-${tournamentId}`;

  const suggestionsDiv = document.getElementById(suggestionsId);
  if (!suggestionsDiv) return;

  const players = activeTab === PLAYER_DATABASES.FIDE ? fidePlayers : swissPlayers;

  let contentHtml: string;
  if (players.length === 0) {
    let noResultsMsg: string;
    if (fieldType === 'firstname') {
      if (activeTab === PLAYER_DATABASES.FIDE) {
        noResultsMsg = t('player.firstNameSearchSwissOnly', 'First name search requires Swiss Chess tab with club filter');
      } else if (!clubFilter) {
        noResultsMsg = t('player.firstNameNeedsClub', 'Enter a club name above to search by first name');
      } else {
        noResultsMsg = t('player.noPlayersInClub', 'No players found matching this first name in the club');
      }
    } else {
      noResultsMsg = activeTab === PLAYER_DATABASES.SWISS_CHESS && clubFilter
        ? t('player.noPlayersInClub', 'No players found in this club')
        : t('player.noPlayersFound', 'No players found');
    }
    contentHtml = `<div class="field-autocomplete-empty">${noResultsMsg}</div>`;
  } else {
    contentHtml = players.map(p => {
      const displayName = `${escapeHtml(p.firstName)} ${escapeHtml(p.lastName)}`.trim();
      const details = [
        p.rating ? `${p.rating}` : '',
        p.federation ? escapeHtml(p.federation) : '',
        p.club ? escapeHtml(p.club) : ''
      ].filter(Boolean).join(' · ');

      return `
        <div class="field-autocomplete-item" data-player='${escapeHtml(JSON.stringify(p))}'>
          <div class="field-autocomplete-name">${p.title ? `<span class="player-title-badge">${escapeHtml(p.title)}</span> ` : ''}${displayName}</div>
          ${details ? `<div class="field-autocomplete-details">${details}</div>` : ''}
        </div>
      `;
    }).join('');
  }

  // Only update content area, not the header (to preserve focus on club filter)
  const contentDiv = suggestionsDiv.querySelector('.field-autocomplete-content');
  if (contentDiv) {
    contentDiv.innerHTML = contentHtml;
  } else {
    // Fallback: render full dropdown if content div doesn't exist
    const headerHtml = buildDropdownHeader(tournamentId, fieldType, activeTab, clubFilter);
    suggestionsDiv.innerHTML = headerHtml + `<div class="field-autocomplete-content">${contentHtml}</div>`;
    const input = document.getElementById(inputId) as HTMLInputElement | null;
    const currentSearchTerm = input?.value.trim() || '';
    setupHeaderHandlers(tournamentId, fieldType, currentSearchTerm);
  }

  // Add click handlers for player items
  suggestionsDiv.querySelectorAll('.field-autocomplete-item').forEach(item => {
    item.addEventListener('click', () => {
      const playerData = (item as HTMLElement).dataset['player'];
      if (playerData) {
        const player = JSON.parse(playerData) as PlayerSearchResult;
        selectPlayer(player, tournamentId, fieldType);
      }
    });
  });
}

/**
 * Select a player from suggestions - populates all fields
 */
function selectPlayer(player: PlayerSearchResult, tournamentId: string, fieldType: FieldType): void {
  const firstNameInput = document.getElementById(`player-first-name-${tournamentId}`) as HTMLInputElement | null;
  const lastNameInput = document.getElementById(`player-last-name-${tournamentId}`) as HTMLInputElement | null;
  const ratingInput = document.getElementById(`player-rating-${tournamentId}`) as HTMLInputElement | null;
  const titleInput = document.getElementById(`player-title-${tournamentId}`) as HTMLSelectElement | null;
  const federationInput = document.getElementById(`player-federation-${tournamentId}`) as HTMLInputElement | null;
  const clubInput = document.getElementById(`player-club-${tournamentId}`) as HTMLInputElement | null;
  const fideIdInput = document.getElementById(`player-fide-id-${tournamentId}`) as HTMLInputElement | null;
  const federationCodeInput = document.getElementById(`player-federation-code-${tournamentId}`) as HTMLInputElement | null;

  const suggestionsId = `${fieldType}-suggestions-${tournamentId}`;
  const suggestionsDiv = document.getElementById(suggestionsId);

  // Get appropriate rating based on tournament time control
  const timeControl = getTournamentTimeControl(tournamentId);
  const rating = getRatingForTimeControl(player, timeControl);

  // Populate all fields
  if (firstNameInput) firstNameInput.value = player.firstName || '';
  if (lastNameInput) lastNameInput.value = player.lastName || '';
  if (ratingInput) ratingInput.value = rating ? String(rating) : '';
  if (titleInput) titleInput.value = player.title || '';
  if (federationInput) federationInput.value = player.federation || '';
  if (clubInput) clubInput.value = player.club || '';
  if (fideIdInput) fideIdInput.value = player.fideId || '';
  if (federationCodeInput) federationCodeInput.value = player.federationCode || '';

  // Store IDs on add button as backup
  const addButton = document.getElementById(`add-player-${tournamentId}`) as HTMLButtonElement | null;
  if (addButton) {
    if (player.federation) addButton.dataset['federation'] = player.federation;
    if (player.fideId) addButton.dataset['fideId'] = player.fideId;
    if (player.federationCode) addButton.dataset['federationCode'] = player.federationCode;
  }

  // Hide suggestions
  if (suggestionsDiv) {
    suggestionsDiv.style.display = 'none';
  }

  // Also hide the other field's suggestions
  const otherFieldType = fieldType === 'firstname' ? 'lastname' : 'firstname';
  const otherSuggestionsDiv = document.getElementById(`${otherFieldType}-suggestions-${tournamentId}`);
  if (otherSuggestionsDiv) {
    otherSuggestionsDiv.style.display = 'none';
  }

  // Focus on rating field if empty, otherwise add button
  if (ratingInput && !ratingInput.value) {
    ratingInput.focus();
  } else if (addButton) {
    addButton.focus();
  }
}

/**
 * Setup autocomplete for firstName field
 */
export function setupFirstNameAutocomplete(tournamentId: string): void {
  setupNameFieldAutocomplete(tournamentId, 'firstname');
}

/**
 * Setup autocomplete for lastName field
 */
export function setupLastNameAutocomplete(tournamentId: string): void {
  setupNameFieldAutocomplete(tournamentId, 'lastname');
}

/**
 * Setup autocomplete for FIDE ID field
 */
export function setupFideIdAutocomplete(tournamentId: string): void {
  const inputId = `player-fide-id-${tournamentId}`;
  const suggestionsId = `fideid-suggestions-${tournamentId}`;

  const input = document.getElementById(inputId) as HTMLInputElement | null;
  const suggestionsDiv = document.getElementById(suggestionsId);

  if (!input || !suggestionsDiv) return;

  let searchTimeout: number | null = null;

  input.addEventListener('input', (e) => {
    const target = e.target as HTMLInputElement;
    const searchTerm = target.value.trim();

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // FIDE IDs are typically 5+ digits
    if (searchTerm.length < 5 || !/^\d+$/.test(searchTerm)) {
      suggestionsDiv.style.display = 'none';
      return;
    }

    searchTimeout = window.setTimeout(async () => {
      await searchByFideId(searchTerm, tournamentId);
    }, 400);
  });

  // Close suggestions when clicking outside
  document.addEventListener('click', (e) => {
    const target = e.target as Node;
    if (!input.contains(target) && !suggestionsDiv.contains(target)) {
      suggestionsDiv.style.display = 'none';
    }
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      suggestionsDiv.style.display = 'none';
    }
  });
}

/**
 * Search FIDE database by ID
 */
async function searchByFideId(fideId: string, tournamentId: string): Promise<void> {
  const suggestionsId = `fideid-suggestions-${tournamentId}`;
  const inputId = `player-fide-id-${tournamentId}`;

  const suggestionsDiv = document.getElementById(suggestionsId);
  const input = document.getElementById(inputId);

  if (!suggestionsDiv || !input) return;

  // Position the dropdown
  positionDropdown(input, suggestionsDiv);

  suggestionsDiv.innerHTML = '<div class="field-autocomplete-content"><div class="field-autocomplete-loading">Searching FIDE...</div></div>';
  suggestionsDiv.style.display = 'block';

  try {
    const cacheKey = `fide:id:${fideId}`;
    let players: PlayerSearchResult[];

    if (fieldSearchCache.has(cacheKey)) {
      players = fieldSearchCache.get(cacheKey)!;
    } else {
      const response = await fetch(`${API_BASE_URL}/fide-search?query=${encodeURIComponent(fideId)}`, {
        credentials: 'include',
      });
      const html = await response.text();
      players = parseFideResults(html);
      // Filter to only players whose FIDE ID starts with the search term
      players = players.filter(p => p.fideId && p.fideId.startsWith(fideId));
      fieldSearchCache.set(cacheKey, players);
    }

    // Display results
    let contentHtml: string;
    if (players.length === 0) {
      contentHtml = `<div class="field-autocomplete-empty">${t('player.noPlayersFound', 'No players found')}</div>`;
    } else {
      contentHtml = players.map(p => {
        const displayName = `${escapeHtml(p.firstName)} ${escapeHtml(p.lastName)}`.trim();
        const details = [
          p.rating ? `${p.rating}` : '',
          p.federation ? escapeHtml(p.federation) : '',
          p.fideId ? `FIDE: ${p.fideId}` : ''
        ].filter(Boolean).join(' · ');

        return `
          <div class="field-autocomplete-item" data-player='${escapeHtml(JSON.stringify(p))}'>
            <div class="field-autocomplete-name">${p.title ? `<span class="player-title-badge">${escapeHtml(p.title)}</span> ` : ''}${displayName}</div>
            ${details ? `<div class="field-autocomplete-details">${details}</div>` : ''}
          </div>
        `;
      }).join('');
    }

    suggestionsDiv.innerHTML = `<div class="field-autocomplete-content">${contentHtml}</div>`;

    // Add click handlers
    suggestionsDiv.querySelectorAll('.field-autocomplete-item').forEach(item => {
      item.addEventListener('click', () => {
        const playerData = (item as HTMLElement).dataset['player'];
        if (playerData) {
          const player = JSON.parse(playerData) as PlayerSearchResult;
          selectPlayerFromFideId(player, tournamentId);
        }
      });
    });
  } catch (error) {
    console.error('Error searching by FIDE ID:', error);
    suggestionsDiv.innerHTML = '<div class="field-autocomplete-content"><div class="field-autocomplete-error">Search error</div></div>';
  }
}

/**
 * Select a player from FIDE ID suggestions
 */
function selectPlayerFromFideId(player: PlayerSearchResult, tournamentId: string): void {
  const firstNameInput = document.getElementById(`player-first-name-${tournamentId}`) as HTMLInputElement | null;
  const lastNameInput = document.getElementById(`player-last-name-${tournamentId}`) as HTMLInputElement | null;
  const ratingInput = document.getElementById(`player-rating-${tournamentId}`) as HTMLInputElement | null;
  const titleInput = document.getElementById(`player-title-${tournamentId}`) as HTMLSelectElement | null;
  const federationInput = document.getElementById(`player-federation-${tournamentId}`) as HTMLInputElement | null;
  const clubInput = document.getElementById(`player-club-${tournamentId}`) as HTMLInputElement | null;
  const fideIdInput = document.getElementById(`player-fide-id-${tournamentId}`) as HTMLInputElement | null;
  const federationCodeInput = document.getElementById(`player-federation-code-${tournamentId}`) as HTMLInputElement | null;
  const suggestionsDiv = document.getElementById(`fideid-suggestions-${tournamentId}`);

  // Get appropriate rating based on tournament time control
  const timeControl = getTournamentTimeControl(tournamentId);
  const rating = getRatingForTimeControl(player, timeControl);

  // Populate all fields
  if (firstNameInput) firstNameInput.value = player.firstName || '';
  if (lastNameInput) lastNameInput.value = player.lastName || '';
  if (ratingInput) ratingInput.value = rating ? String(rating) : '';
  if (titleInput) titleInput.value = player.title || '';
  if (federationInput) federationInput.value = player.federation || '';
  if (clubInput) clubInput.value = player.club || '';
  if (fideIdInput) fideIdInput.value = player.fideId || '';
  if (federationCodeInput) federationCodeInput.value = player.federationCode || '';

  // Store IDs on add button
  const addButton = document.getElementById(`add-player-${tournamentId}`) as HTMLButtonElement | null;
  if (addButton) {
    if (player.federation) addButton.dataset['federation'] = player.federation;
    if (player.fideId) addButton.dataset['fideId'] = player.fideId;
    if (player.federationCode) addButton.dataset['federationCode'] = player.federationCode;
  }

  // Hide suggestions
  if (suggestionsDiv) {
    suggestionsDiv.style.display = 'none';
  }

  // Focus on add button
  if (addButton) {
    addButton.focus();
  }
}

/**
 * Initialize field autocomplete for a tournament
 */
export function setupFieldAutocompletes(tournamentId: string): void {
  setupLastNameAutocomplete(tournamentId);
  setupFideIdAutocomplete(tournamentId);
}

// Export to window for global access
window.setupFieldAutocompletes = setupFieldAutocompletes;
window.setupFirstNameAutocomplete = setupFirstNameAutocomplete;
window.setupLastNameAutocomplete = setupLastNameAutocomplete;
window.setupFideIdAutocomplete = setupFideIdAutocomplete;
